/**
 * ReaderSettingsPanel Component
 *
 * Slide-out settings panel for the reader:
 * - Translation version picker
 * - Clear highlights button
 * - Clear reading position
 * - Offline status indicator
 */

import { memo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Trash2, Bookmark, Wifi, WifiOff, ChevronRight } from 'lucide-react';
import type { ReaderSettingsPanelProps, SefariaVersion } from '@/types/sefaria';

// ============================================================
// Version Picker Item
// ============================================================

interface VersionItemProps {
  version: SefariaVersion;
  isSelected: boolean;
  onSelect: () => void;
}

function VersionItem({ version, isSelected, onSelect }: VersionItemProps) {
  return (
    <button
      onClick={onSelect}
      className={`
        w-full px-4 py-3 flex items-center justify-between text-left transition-colors
        ${isSelected ? 'bg-[#f8e8f8] border-l-4 border-[#8e4585]' : 'hover:bg-slate-50'}
      `}
    >
      <div>
        <p className="font-medium text-slate-700">
          {version.shortVersionTitle || version.versionTitle}
        </p>
        {version.license && <p className="text-xs text-slate-400 mt-0.5">{version.license}</p>}
      </div>
      {isSelected && <Check size={20} className="text-[#8e4585]" />}
    </button>
  );
}

// ============================================================
// Main Component
// ============================================================

function ReaderSettingsPanelComponent({
  isOpen,
  versions,
  selectedVersion,
  highlightedCount,
  isAvailableOffline,
  isOffline,
  isUsingCache,
  onVersionChange,
  onClearHighlights,
  onClearPosition,
  onClose,
}: ReaderSettingsPanelProps) {
  if (!isOpen) return null;

  const onlineConnectionStatus = isUsingCache
    ? 'Using cached data for faster loading'
    : 'Reading from Sefaria';
  const connectionStatusText = isOffline ? 'Reading from cached data' : onlineConnectionStatus;

  const verseSuffix = highlightedCount !== 1 ? 's' : '';
  const clearHighlightsSubtext =
    highlightedCount > 0 ? `${highlightedCount} verse${verseSuffix} highlighted` : 'No highlights';

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[101]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-white shadow-2xl z-[102] flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#63345e] text-white px-4 py-4 flex items-center justify-between shrink-0">
              <h2 className="font-bold text-lg">Reader Settings</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[#8e4585] rounded-lg transition-colors"
                aria-label="Close settings"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Connection & Cache Status */}
              <div className="px-4 py-4 border-b border-slate-100 space-y-3">
                {/* Current connection status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isOffline ? (
                      <WifiOff size={20} className="text-yellow-500" />
                    ) : (
                      <Wifi size={20} className="text-green-500" />
                    )}
                    <div>
                      <p className="font-medium text-slate-700">
                        {isOffline ? 'Offline Mode' : 'Connected'}
                      </p>
                      <p className="text-sm text-slate-500">{connectionStatusText}</p>
                    </div>
                  </div>
                  <div
                    className={`w-3 h-3 rounded-full ${isOffline ? 'bg-yellow-500' : 'bg-green-500'}`}
                  />
                </div>

                {/* Offline availability status */}
                <div className="flex items-center gap-3 text-sm text-slate-500 pl-8">
                  <div
                    className={`w-2 h-2 rounded-full ${isAvailableOffline ? 'bg-green-400' : 'bg-slate-300'}`}
                  />
                  <span>
                    {isAvailableOffline
                      ? 'This parsha is saved for offline reading'
                      : 'Not cached for offline use'}
                  </span>
                </div>
              </div>

              {/* Translation Versions */}
              {versions.length > 0 && (
                <div className="border-b border-slate-100">
                  <div className="px-4 py-3 bg-slate-50">
                    <h3 className="font-bold text-sm uppercase tracking-wider text-slate-500">
                      Translation
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {versions.map((version) => (
                      <VersionItem
                        key={version.versionTitle}
                        version={version}
                        isSelected={selectedVersion === version.versionTitle}
                        onSelect={() => onVersionChange(version.versionTitle)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="px-4 py-4 space-y-3">
                <h3 className="font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">
                  Actions
                </h3>

                {/* Clear Highlights */}
                <button
                  onClick={onClearHighlights}
                  disabled={highlightedCount === 0}
                  className={`
                    w-full px-4 py-3 flex items-center justify-between rounded-lg transition-colors
                    ${
                      highlightedCount > 0
                        ? 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                        : 'bg-slate-50 text-slate-400 cursor-not-allowed'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    <Trash2 size={20} />
                    <div className="text-left">
                      <p className="font-medium">Clear Highlights</p>
                      <p className="text-sm text-slate-500">{clearHighlightsSubtext}</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </button>

                {/* Clear Reading Position */}
                <button
                  onClick={onClearPosition}
                  className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Bookmark size={20} className="text-slate-500" />
                    <div className="text-left">
                      <p className="font-medium text-slate-700">Clear Reading Position</p>
                      <p className="text-sm text-slate-500">Start from the beginning</p>
                    </div>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-slate-50 text-center text-xs text-slate-400 shrink-0">
              Powered by Sefaria
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}

export const ReaderSettingsPanel = memo(ReaderSettingsPanelComponent);
